import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
    Box,
    Typography,
    Button,
    Dialog,
    DialogContent,
    IconButton,
} from "@mui/material";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import CloseIcon from "@mui/icons-material/Close";
import { decryptTheParams, encryptTheParams } from "../../Helper/QueryParams/EncryptDecrypt";
import { usePermissions } from "../../Context/PermissionContext";
import { FEATURE_CODES } from "../../Static/FeatureMapping";
import { getPagesMetaData } from "../../Api/Api";
import {
    getMetricsPreview,
    createServiceRecord,
    getBatteryComponents,
} from "../../Api/ServiceRecordApi";
import { transformTwinHierarchyData } from "../../Helper/DigitalTwin/DigitalTwinFunctions";
import { extractStringsWithPacks } from "../../Helper/DigitalTwin/BatteryComponentsHelper";
import {
    convertZonetoUTC,
    convertZonetoUTCStart,
    getLocalDateFromUTC,
} from "../../Helper/DatePicker/DateConverter";
import { useToast } from "../../Hooks/useToast";
import CartLoader from "../../Helper/Loaders/CartLoader";
import ScreenContainer from "../../Components/ScreenContainer";
import ProblemReportedSection from "./form/ProblemReportedSection";
import RecordReplacementReview from "./review/RecordReplacementReview";
import PackReplacementCard from "./form/PackReplacementCard";
import AddPackMenu from "./form/AddPackMenu";
import TimelineSection from "./form/TimelineSection";
import SupportingDocumentsSection from "./form/SupportingDocumentsSection";
import BusInformationSection from "./form/BusInformationSection";
import RemarksSection from "./form/RemarksSection";
import {
    validateRecordReplacement,
    getFirstErrorAnchor,
    FORM_ANCHORS,
    buildCreatePayload,
    createPackFormEntry,
} from "./recordReplacementHelper";

const CUSTOM_MAX = 150;

export default function RecordReplacementPage() {
    const navigate = useNavigate();
    const { t } = useTranslation(["digital_twin"]);
    const { openNotification } = useToast();
    const { hasPermission, isRbacV2Enabled } = usePermissions();

    const params = useMemo(() => decryptTheParams(), []);
    const { batteryID, absPackNos, lastNonOperationalAt, vehicleNumber, pageType } =
        params || {};

    // Slot numbers arrive as a comma list (URL params are flat strings). Everything
    // else about each pack -- serial, install date, packConfigID, labels -- is
    // resolved from the components tree below rather than carried in the URL, so the
    // form always works from the current state of the bus.
    const initialAbsPackNos = useMemo(
        () =>
            String(absPackNos || "")
                .split(",")
                .map((value) => value.trim())
                .filter(Boolean),
        [absPackNos]
    );

    // Components config lives under the digitalTwin page meta (IOT twins use a
    // separate page); mirror DigitalTwin.jsx's pageType -> pageName mapping.
    const metaPageName = pageType === "IOTDigitalTwin" ? "IOTDigitalTwin" : "digitalTwin";

    // "create", matching the endpoints this page posts to and the gate on the buttons
    // that lead here (StringsPacksGrid). While this asked for "update" and the buttons
    // asked for "create", a user holding one and not the other was shown the controls
    // and then bounced to /inventory by the guard below.
    const canEdit = !isRbacV2Enabled()
        ? true
        : hasPermission(FEATURE_CODES.BATTERY_COMPONENTS, "create");

    const [configLoading, setConfigLoading] = useState(true);
    const [config, setConfig] = useState(null);
    const [errors, setErrors] = useState({});
    const [step, setStep] = useState("form");
    const [cancelOpen, setCancelOpen] = useState(false);

    const [snapshotToken, setSnapshotToken] = useState(null);
    const [preview, setPreview] = useState(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const [strings, setStrings] = useState([]);
    const [packsLoading, setPacksLoading] = useState(true);

    const [formState, setFormState] = useState(() => ({
        problemsReported: [],
        customProblems: [],
        // One entry per pack card. Seeded from the components tree once it loads.
        packs: [],
        timeline: {
            // Pre-fill "Issue reported date time" with the battery's last
            // non-operational time (backend UTC -> user-zone wall-clock for the
            // picker). The user can still change it if the issue was reported
            // earlier. null when the battery was never marked non-operational.
            busHeldUpAt: lastNonOperationalAt
                ? getLocalDateFromUTC(lastNonOperationalAt, "YYYY-MM-DD HH:mm:ss")
                : null,
            workStartAt: null,
            workEndAt: null,
        },
        remarks: "",
        acknowledged: false,
        documents: {},
    }));

    // Direct-URL guard: users without the create permission can't record.
    useEffect(() => {
        if (!canEdit) navigate("/inventory", { replace: true });
    }, [canEdit, navigate]);

    // Load the org's replacement config (option lists, doc types).
    useEffect(() => {
        let active = true;
        (async () => {
            setConfigLoading(true);
            try {
                const res = await getPagesMetaData(metaPageName);
                const replacement =
                    res?.response?.response?.metaInfo?.components?.replacement || null;
                // Business rule: every supporting document is mandatory except the
                // "Other document" (OTHER), regardless of the orgMeta flag.
                if (replacement?.documentTypes) {
                    replacement.documentTypes = replacement.documentTypes.map((dt) => ({
                        ...dt,
                        required: dt.code !== "OTHER",
                    }));
                }
                if (active) setConfig(replacement);
            } catch (err) {
                console.error("Failed to load replacement config:", err);
            } finally {
                if (active) setConfigLoading(false);
            }
        })();
        return () => {
            active = false;
        };
    }, [metaPageName]);

    // Components tree: resolves the selected slots into pack cards, and supplies the
    // "Add another pack" list. Read here rather than passed through the URL so the
    // serials are the bus's current ones -- a serial captured at navigation time can
    // already be retired, which is what makes a re-replace 409.
    useEffect(() => {
        if (!batteryID) {
            setPacksLoading(false);
            return;
        }
        let active = true;
        (async () => {
            setPacksLoading(true);
            try {
                const res = await getBatteryComponents(batteryID);
                if (!active) return;
                const tree = transformTwinHierarchyData(res?.response?.BATTERY_SYSTEM || [], t);
                setStrings(extractStringsWithPacks(tree));
            } catch (err) {
                console.error("Failed to load battery components:", err);
                if (active) setStrings([]);
            } finally {
                if (active) setPacksLoading(false);
            }
        })();
        return () => {
            active = false;
        };
    }, [batteryID, t]);

    // Every pack on the bus, flattened with its string label — the pool the cards and
    // the "Add another pack" menu are drawn from, in slot order.
    const allPacks = useMemo(() => {
        const packs = [];
        (strings || []).forEach((string) =>
            (string.packs || []).forEach((pack) =>
                packs.push({ ...pack, stringLabel: string.label || "" })
            )
        );
        return packs;
    }, [strings]);

    // Seed the cards from the slots the user picked, once the tree is in. Slots that
    // no longer exist on the bus are dropped rather than rendered as empty cards.
    const seededRef = useRef(false);
    useEffect(() => {
        if (seededRef.current || !allPacks.length) return;
        seededRef.current = true;
        const selected = allPacks.filter((pack) =>
            initialAbsPackNos.includes(String(pack.absPackNo))
        );
        if (!selected.length) return;
        setFormState((prev) => ({
            ...prev,
            packs: selected.map(createPackFormEntry),
        }));
    }, [allPacks, initialAbsPackNos]);

    // Packs not already in the request, for the "Add another pack" menu.
    const availablePacks = useMemo(() => {
        const taken = new Set(formState.packs.map((pack) => String(pack.absPackNo)));
        return allPacks.filter((pack) => !taken.has(String(pack.absPackNo)));
    }, [allPacks, formState.packs]);

    // Snapshot-token flow: fetch two-point metrics whenever busHeldUpAt changes.
    const busHeldUpAt = formState.timeline.busHeldUpAt;
    useEffect(() => {
        if (!busHeldUpAt || !batteryID) {
            setPreview(null);
            setSnapshotToken(null);
            return;
        }
        let active = true;
        (async () => {
            setPreviewLoading(true);
            try {
                // busHeldUpAt is user-local wall-clock (from the picker); the API
                // expects UTC. Convert so the snapshot is captured at the right
                // instant and stays consistent with the final submit below.
                const res = await getMetricsPreview(batteryID, convertZonetoUTC(busHeldUpAt));
                const data = res?.response?.data ?? res?.response ?? null;
                if (active) {
                    setPreview(data);
                    setSnapshotToken(data?.snapshotToken || null);
                }
            } catch (err) {
                console.error("Failed to load metrics preview:", err);
                if (active) {
                    setPreview(null);
                    setSnapshotToken(null);
                }
            } finally {
                if (active) setPreviewLoading(false);
            }
        })();
        return () => {
            active = false;
        };
    }, [busHeldUpAt, batteryID]);

    // ---- updaters ----
    // Patch one side of one pack card, addressed by its key.
    const patchPack = (key, side, patch) =>
        setFormState((prev) => ({
            ...prev,
            packs: prev.packs.map((pack) =>
                pack.key === key ? { ...pack, [side]: { ...pack[side], ...patch } } : pack
            ),
        }));

    // One scroll mechanism for two jobs: showing a pack that was just added, and
    // taking the user to the first thing a failed submit objected to. Both are the
    // same need -- put one part of a form taller than the screen in front of them.
    const packCardRefs = useRef({});
    const sectionRefs = useRef({});
    const [revealAnchor, setRevealAnchor] = useState(null);

    // Cards are kept in slot order, so an added pack rarely appears where the user
    // just clicked -- Pack 2 lands above Pack 5, often above the fold.
    const addPack = (pack) => {
        const entry = createPackFormEntry(pack);
        setFormState((prev) => ({
            ...prev,
            packs: [...prev.packs, entry].sort(
                (a, b) => Number(a.absPackNo) - Number(b.absPackNo)
            ),
        }));
        setRevealAnchor(`pack:${entry.key}`);
    };

    // Runs after the render that produced the node, so there is something to scroll
    // to. Cleared immediately: a one-shot reaction to an action, not a state the page
    // should re-apply on every later render.
    useEffect(() => {
        if (!revealAnchor) return;
        const node = revealAnchor.startsWith("pack:")
            ? packCardRefs.current[revealAnchor.slice("pack:".length)]
            : sectionRefs.current[revealAnchor];
        node?.scrollIntoView({ behavior: "smooth", block: "center" });
        setRevealAnchor(null);
    }, [revealAnchor]);

    const removePack = (pack) => {
        // The ref itself is dropped by the callback below when React unmounts the card
        // with null -- deleting here would only be re-inserted by that same call.
        setFormState((prev) => ({
            ...prev,
            packs: prev.packs.filter((entry) => entry.key !== pack.key),
        }));
    };

    const setTimeline = (patch) =>
        setFormState((prev) => ({ ...prev, timeline: { ...prev.timeline, ...patch } }));
    const onDocChange = (code, info) =>
        setFormState((prev) => ({ ...prev, documents: { ...prev.documents, [code]: info } }));

    const toggleProblem = (code) =>
        setFormState((prev) => ({
            ...prev,
            problemsReported: prev.problemsReported.includes(code)
                ? prev.problemsReported.filter((c) => c !== code)
                : [...prev.problemsReported, code],
        }));
    // Only one custom problem reason is allowed.
    const addCustom = () =>
        setFormState((prev) =>
            prev.customProblems.length >= 1
                ? prev
                : { ...prev, customProblems: [...prev.customProblems, ""] }
        );
    const changeCustom = (index, val) =>
        setFormState((prev) => {
            const next = [...prev.customProblems];
            next[index] = val.slice(0, CUSTOM_MAX);
            return { ...prev, customProblems: next };
        });
    const removeCustom = (index) =>
        setFormState((prev) => ({
            ...prev,
            customProblems: prev.customProblems.filter((_, i) => i !== index),
        }));


    const handleRecord = () => {
        const { errors: errs, isValid } = validateRecordReplacement(formState, config || {});
        setErrors(errs);
        if (!isValid) {
            openNotification({
                text: "Please complete all required fields",
                icon: "toast-error",
            });
            // A highlighted field can sit well outside the viewport on a form this
            // tall, leaving the toast as the only sign anything is wrong. Take them
            // to the first section that objected.
            setRevealAnchor(getFirstErrorAnchor(errs, formState.packs));
            return;
        }
        if (!snapshotToken) {
            openNotification({
                text: "Bus information is still loading, please wait",
                icon: "toast-error",
            });
            return;
        }
        setStep("review");
    };

    const handleConfirmSubmit = async () => {
        if (submitting) return;
        setSubmitting(true);
        try {
            const payload = buildCreatePayload({ formState, batteryID, snapshotToken });
            // Timeline datetimes are user-local wall-clock (from the picker); the
            // API/DB store UTC, so convert before sending. Uses the same
            // conversion as the metrics-preview busHeldUpAt above so the snapshot
            // token stays consistent.
            const sd = payload.serviceDetails;
            sd.busHeldUpAt = sd.busHeldUpAt ? convertZonetoUTC(sd.busHeldUpAt) : sd.busHeldUpAt;
            sd.workStartAt = sd.workStartAt ? convertZonetoUTC(sd.workStartAt) : sd.workStartAt;
            sd.workEndAt = sd.workEndAt ? convertZonetoUTC(sd.workEndAt) : sd.workEndAt;
            // A captured install date is a user-zone calendar day, but packConfig.
            // installedOn is a UTC DATETIME (the incoming pack's copy is the work-end
            // instant), so it has to cross the wire as UTC like the timeline above.
            // Sending the bare local day would read back one day early west of UTC.
            // Every pack in the request carries its own, so convert them all.
            // incomingManufacturingDate stays untouched — that column is a real DATE.
            payload.packReplacementDetails.forEach((pack) => {
                if (pack.outgoingInstalledOn) {
                    pack.outgoingInstalledOn = convertZonetoUTCStart(pack.outgoingInstalledOn);
                }
            });
            await createServiceRecord(payload);
            openNotification({
                text: "Replacement recorded successfully",
                icon: "toast-success",
            });
            // Redirect to the battery's Digital Twin page, Components tab (its
            // sub-tab defaults to "Battery"), so the user sees the updated packs.
            encryptTheParams(
                { batteryID, tabValue: "components", pageType: pageType || "" },
                navigate,
                true,
            );
        } catch (err) {
            console.error("Create service record failed:", err);
            const status = err?.response?.status;
            const backendMsg = err?.response?.data?.responseStatus?.msg;
            if (status === 409) {
                // 409 covers a stale outgoing serial AND a duplicate incoming serial
                // (already active elsewhere in the org) — surface the backend's
                // specific message so the user knows which and can correct it.
                openNotification({
                    text:
                        backendMsg ||
                        "The pack serial changed since you opened this form. Go back and refresh.",
                    icon: "toast-error",
                });
            } else {
                openNotification({
                    text: "Failed to record replacement. Please try again.",
                    icon: "toast-error",
                });
            }
        } finally {
            setSubmitting(false);
        }
    };

    const handleCancelConfirm = () => {
        setCancelOpen(false);
        // This screen is opened in its own browser tab; on cancel, close the tab.
        window.close();
    };

    // Only used from the review step to return to the form for editing.
    const handleBack = () => setStep("form");

    // On the form step the CTA stays active: clicking runs validation and surfaces
    // inline errors under the unfilled required fields (handleRecord). Only the
    // review step disables while submitting.
    const primaryDisabled = step === "form" ? false : submitting;
    const primaryLabel = step === "form" ? "Record Replacement" : "Confirm Record";
    const onPrimaryClick = step === "form" ? handleRecord : handleConfirmSubmit;

    if (configLoading || packsLoading) {
        return (
            <ScreenContainer pagesContent={null}>
                <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "60vh" }}>
                    <CartLoader />
                </Box>
            </ScreenContainer>
        );
    }

    return (
        <ScreenContainer pagesContent={null}>
            <Box sx={{ px: "24px", pt: "14px", pb: "80px" }}>
            {/* ---- Top header (breadcrumb + title + actions) — frozen on scroll ---- */}
            <Box
                sx={{
                    position: "sticky",
                    top: "50px",
                    zIndex: (theme) => theme.zIndex.appBar + 1,
                    backgroundColor: "#fff",
                    borderBottom: "1px solid #EAEAEA",
                    mx: "-24px",
                    px: "24px",
                    py: "16px",
                    mb: "4px",
                }}
            >
                <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
                    <Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: "8px", mb: "6px" }}>
                            <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "#8D8D8D" }}>
                                FLEET
                            </Typography>
                            <Typography sx={{ fontSize: "13px", color: "#8D8D8D" }}>/</Typography>
                            <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "#4A4AF4" }}>
                                {vehicleNumber || batteryID}
                            </Typography>
                            {formState.packs.length > 0 && (
                                <Box
                                    data-testid="request-pack-count"
                                    sx={{
                                        fontSize: "12px",
                                        color: "#494949",
                                        backgroundColor: "#F0F0F0",
                                        borderRadius: "4px",
                                        padding: "2px 10px",
                                    }}
                                >
                                    {formState.packs.length === 1
                                        ? formState.packs[0].stringLabel +
                                          " " +
                                          formState.packs[0].packLabel
                                        : `${formState.packs.length} packs`}
                                </Box>
                            )}
                        </Box>
                        <Typography sx={{ fontSize: "26px", fontWeight: 700, color: "#1A1A1A" }}>
                            Record Replacement
                        </Typography>
                    </Box>

                    <Box sx={{ display: "flex", gap: "12px" }}>
                        <Button
                            data-testid="record-cancel"
                            onClick={() => setCancelOpen(true)}
                            sx={{
                                textTransform: "none",
                                fontSize: "14px",
                                fontWeight: 500,
                                color: "#494949",
                                border: "1px solid #D0D0D0",
                                borderRadius: "8px",
                                padding: "8px 22px",
                                "&:hover": { backgroundColor: "#F5F5F5" },
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            data-testid="record-submit-top"
                            disableElevation
                            disabled={primaryDisabled}
                            onClick={onPrimaryClick}
                            sx={{
                                textTransform: "none",
                                fontSize: "14px",
                                fontWeight: 600,
                                color: "#fff",
                                backgroundColor: "#6D72F6",
                                borderRadius: "8px",
                                padding: "8px 22px",
                                "&:hover": { backgroundColor: "#5A5FE0" },
                                "&.Mui-disabled": { backgroundColor: "#D8D8D8", color: "#fff" },
                            }}
                        >
                            {primaryLabel}
                        </Button>
                    </Box>
                </Box>
            </Box>

            {/* ---- Sub-breadcrumb (back shows only while reviewing, to return to the form) ---- */}
            <Box sx={{ display: "flex", alignItems: "center", gap: "8px", py: "16px" }}>
                {step === "review" && (
                    <IconButton size="small" onClick={handleBack} data-testid="record-back">
                        <KeyboardArrowLeftIcon sx={{ color: "#494949" }} />
                    </IconButton>
                )}
                <Typography sx={{ fontSize: "15px", color: "#8D8D8D" }}>
                    Digital Twin/Component/{" "}
                    <span style={{ color: "#1A1A1A", fontWeight: 600 }}>Record Replacement</span>
                </Typography>
            </Box>

            {/* ---- Body: single outer container holding all section blocks ---- */}
            <Box
                sx={{
                    border: "1px solid #EAEAEA",
                    borderRadius: "16px",
                    backgroundColor: "#fff",
                    padding: "16px",
                    display: "flex",
                    flexDirection: "column",
                    gap: "20px",
                }}
            >
                {step === "form" ? (
                    <>
                        <Box
                            ref={(node) => {
                                sectionRefs.current[FORM_ANCHORS.problemsReported] = node;
                            }}
                        >
                            <ProblemReportedSection
                                options={config?.problemsReported || []}
                                allowCustom={config?.allowCustomProblem !== false}
                                selectedCodes={formState.problemsReported}
                                customProblems={formState.customProblems}
                                onToggleCode={toggleProblem}
                                onAddCustom={addCustom}
                                onChangeCustom={changeCustom}
                                onRemoveCustom={removeCustom}
                                hasError={!!errors.problemsReported}
                            />
                        </Box>

                        {formState.packs.map((pack) => (
                            <PackReplacementCard
                                key={pack.key}
                                pack={pack}
                                config={config || {}}
                                batteryID={batteryID}
                                errors={errors.packs?.[pack.key] || {}}
                                canRemove={formState.packs.length > 1}
                                onChangeOutgoing={(patch) => patchPack(pack.key, "outgoing", patch)}
                                onChangeIncoming={(patch) => patchPack(pack.key, "incoming", patch)}
                                onRemove={removePack}
                                cardRef={(node) => {
                                    // React calls this with null on unmount, which is
                                    // the only point the entry can actually be removed.
                                    if (node) packCardRefs.current[pack.key] = node;
                                    else delete packCardRefs.current[pack.key];
                                }}
                            />
                        ))}

                        {errors.noPacks && (
                            <Typography sx={{ fontSize: "13px", color: "#E5484D" }}>
                                Add at least one pack to this request
                            </Typography>
                        )}

                        <Box
                            ref={(node) => {
                                sectionRefs.current[FORM_ANCHORS.packs] = node;
                            }}
                        >
                            <AddPackMenu availablePacks={availablePacks} onAdd={addPack} />
                        </Box>

                        <Box
                            ref={(node) => {
                                sectionRefs.current[FORM_ANCHORS.documents] = node;
                            }}
                        >
                            <SupportingDocumentsSection
                                config={config || {}}
                                batteryID={batteryID}
                                packs={formState.packs}
                                documents={formState.documents}
                                errors={errors}
                                onDocChange={onDocChange}
                            />
                        </Box>

                        <Box
                            ref={(node) => {
                                sectionRefs.current[FORM_ANCHORS.timeline] = node;
                            }}
                        >
                            <TimelineSection
                                timeline={formState.timeline}
                                setTimeline={setTimeline}
                                errors={errors}
                            />
                        </Box>

                        <BusInformationSection
                            preview={preview}
                            loading={previewLoading}
                            busHeldUpAt={busHeldUpAt}
                        />

                        <Box
                            ref={(node) => {
                                sectionRefs.current[FORM_ANCHORS.remarks] = node;
                            }}
                        >
                            <RemarksSection
                                remarks={formState.remarks}
                                acknowledged={formState.acknowledged}
                                errors={errors}
                                onRemarksChange={(val) =>
                                    setFormState((prev) => ({ ...prev, remarks: val }))
                                }
                                onAcknowledgedChange={(val) =>
                                    setFormState((prev) => ({ ...prev, acknowledged: val }))
                                }
                            />
                        </Box>

                        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: "8px" }}>
                            <Button
                                data-testid="record-submit-bottom"
                                disableElevation
                                disabled={primaryDisabled}
                                onClick={handleRecord}
                                sx={{
                                    textTransform: "none",
                                    fontSize: "14px",
                                    fontWeight: 600,
                                    color: "#fff",
                                    backgroundColor: "#6D72F6",
                                    borderRadius: "8px",
                                    padding: "10px 26px",
                                    "&:hover": { backgroundColor: "#5A5FE0" },
                                    "&.Mui-disabled": { backgroundColor: "#D8D8D8", color: "#fff" },
                                }}
                            >
                                Record Replacement
                            </Button>
                        </Box>
                    </>
                ) : (
                    <RecordReplacementReview
                        formState={formState}
                        config={config || {}}
                        preview={preview}
                    />
                )}
            </Box>

            {/* ---- Cancel warning modal ---- */}
            <Dialog
                open={cancelOpen}
                onClose={() => setCancelOpen(false)}
                PaperProps={{ sx: { borderRadius: "16px", maxWidth: "460px" } }}
            >
                <DialogContent sx={{ position: "relative", p: "32px", textAlign: "center" }}>
                    <IconButton
                        onClick={() => setCancelOpen(false)}
                        sx={{ position: "absolute", top: 12, right: 12, backgroundColor: "#F0F0F0" }}
                        size="small"
                    >
                        <CloseIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                    <Typography sx={{ fontSize: "20px", fontWeight: 700, color: "#1A1A1A", mb: "10px" }}>
                        Attention! Cancelling replacement record
                    </Typography>
                    <Typography sx={{ fontSize: "15px", color: "#8D8D8D", mb: "24px" }}>
                        All the fields you filled won't be saved, are you sure you want to go back ?
                    </Typography>
                    <Box sx={{ display: "flex", justifyContent: "center", gap: "12px" }}>
                        <Button
                            onClick={() => setCancelOpen(false)}
                            data-testid="cancel-modal-dismiss"
                            sx={{
                                textTransform: "none",
                                fontSize: "14px",
                                fontWeight: 500,
                                color: "#494949",
                                border: "1px solid #D0D0D0",
                                borderRadius: "8px",
                                padding: "8px 28px",
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            data-testid="cancel-confirm"
                            disableElevation
                            onClick={handleCancelConfirm}
                            sx={{
                                textTransform: "none",
                                fontSize: "14px",
                                fontWeight: 600,
                                color: "#fff",
                                backgroundColor: "#6D72F6",
                                borderRadius: "8px",
                                padding: "8px 28px",
                                "&:hover": { backgroundColor: "#5A5FE0" },
                            }}
                        >
                            Confirm
                        </Button>
                    </Box>
                </DialogContent>
            </Dialog>
            </Box>
        </ScreenContainer>
    );
}
